import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { css, glyphs } from "../../../Styles";
import { NewSelectList, NewInputList, NewModal, MainContentPaddingTypes, NewSelect } from "../..";
import { ButtonTypes, ListStyle_Types, NewSelectHandles, NewInputListHandles, NewSelectListHandles } from "../../../Definitions";
import { useFeedcurveOptions, useGroupOptions, useSectionOptions } from "../../_Components";
import { Graph_QL, useAnimal } from "../../../Services";
import NewButton from "../../BaseComponents/newButton";

interface AddAnimalModal_Props {
  isModalOpen: boolean
  closeModal: () => void,
  connect: {
    domain: string;
    connection: Graph_QL.Connection;
  }
}

export default function AddAnimalModal({isModalOpen, closeModal, connect}:AddAnimalModal_Props) {
  const {t} = useTranslation()
  const [message, setMessage] = useState<string | null>(null)

  const groupOptions = useGroupOptions()
  const feedcurveOptions = useFeedcurveOptions()
  const sectionOptions = useSectionOptions()

  const inputRef = useRef<NewInputListHandles>(null);
  const selectRef = useRef<NewSelectHandles>(null)
  const selectRefs = useRef<NewSelectListHandles>(null)
  
  const { call: getAnimals, data: animalState, createAnimals: createAnimal } = useAnimal(connect.connection);
  
  useEffect(() => {
    if (!animalState[connect.domain]?.data) {
      getAnimals({})
    }
  },[animalState, connect.domain, getAnimals])
  
  const animals = useMemo(() => {
    return animalState[connect.domain]?.data
  },[animalState, connect.domain]) 

  const handleCreate = () => {
    if (inputRef && inputRef.current && inputRef.current.refs.current) {
      const animalNumber = +inputRef.current.refs.current[0].value;
      const responderNumber = inputRef.current.refs.current[1].value === "" ? undefined: +inputRef.current.refs.current[1].value;
      const lifeNumber = inputRef.current.refs.current[2].value === "" ? undefined : +inputRef.current.refs.current[2].value;
      let location_id;
      // let condition;
      let feedCurve;
      let group;
      // let type;

      if (selectRef && selectRef.current) {
        location_id = selectRef.current.value.id ? Number(selectRef.current.value.id) : undefined;
      }
      
      if (selectRefs && selectRefs.current && selectRefs.current.refs.current) {
        // condition = selectRef.current.refs.current[0]?.value.id
        feedCurve = selectRefs.current.refs.current[1]?.value.id
        group = selectRefs.current.refs.current[2]?.value.id
        // type = selectRefs.current.refs.current[3]?.value.id
      }

      if (!animalNumber) {
        inputRef.current.refs.current[0].setError()
        setMessage(t("animalPages.animals.missingAnimalNo", { defaultValue: "animal no. is missing" }))
      } else if (animals.some(a => a.animal_number === animalNumber)) {
        setMessage(t("animalPages.animals.duplicatedAnimalNo", { defaultValue: "the animal number is already used" }))
      } else if (responderNumber && animals.some(a => a.responder_number === responderNumber)) {
        inputRef.current.refs.current[1].setError()
        setMessage(t("animalPages.animals.duplicatedResponderNo", {defaultValue: "the responder number is already used"}))
      } else {
        if (createAnimal) {
          createAnimal([{
            animal_number: animalNumber, 
            responder_number: responderNumber, // TODO send null instead of 0
            life_number: lifeNumber,
            location_id,
            // condition: condition,
            feedcurve_id: Number(feedCurve),
            animal_group_id: Number(group),
            // animal_type: Number(type)
          }])
          closeModal()
        }
      }
      setTimeout(() => {
        setMessage(null);
      }, 2000);
    }
  }
      
  return (
    <NewModal
      modalState={isModalOpen}
      header={t("animalPages.animals.newAnimal", {defaultValue: "New Animal"})}
      predefinedButtons={{
        onClose: closeModal
      }}
      overrides={{
        mainContentPadding: MainContentPaddingTypes.secondary
      }}
      mainContent={
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "start", justifyContent: "center", columnGap: "40px", rowGap: "10px"}}>
          <div style={{ display: "flex", flexDirection: "column", gap: "10px"}}>
            <NewInputList
              ref={inputRef}
              headers={[t("params.animalId", {defaultValue: "animal no."}), t("params.responderNo", {defaultValue: "responder no."}), t("params.lifeNo", {defaultValue: "life no."})]}
              startEnhancers={[glyphs.AnimalNumber({}), glyphs.ResponderNumber({}), glyphs.LifeNumber({})]}
              placeholders={["", "", ""]}
              isRequired={[true, false, false]}
              valueOverrides={{
                valueTypes: ["number", "number", "number"]
              }}
              listOverrides={{
                listStyle: ListStyle_Types.vertical,
                listGap: "10px"
              }}
            />
            <NewSelect
              ref={selectRef}
              header={t("params.location", {defaultValue: "location"})}
              startEnhancer={glyphs.Location({})}
              options={sectionOptions.locationOptions ?? []}
            />
          </div>
          <NewSelectList
            isDisabled={[true, false, false, true]}
            placeholders={["", "", "", ""]}
            ref={selectRefs}
            startEnhancers={[glyphs.Condition({}), glyphs.FeedCurve({}), glyphs.AnimalGroup({}), glyphs.AnimalType({})]}
            headers={[t("params.condition", {defaultValue: "condition"}), t("params.feedCurve", {defaultValue: "feed curve"}), t("params.group", {defaultValue: "group"}), t("params.animalType", {defaultValue: "animal type"})]}
            options={[[], feedcurveOptions.feedcurveOptions ?? [], groupOptions ?? [], []]}
            listOverrides={{
              listStyle: ListStyle_Types.vertical,
              listGap: "10px"
            }}
          />
        </div>
      }
      footerMessage={message ?? ""}
      footerContent={
        <NewButton 
          content={t("buttons.create", {defaultValue: "create"})}
          buttonType={ButtonTypes.primaryButton}
          overrides={{
            // baseStyles: {...css.button_FitContent}
          }}
          onClick={() => {handleCreate()}}
        />
      }
    />
  );
}